import DefaultLayout from "@/layouts/default-layout";
import { getUserInitials } from "@/utils";
import { useClerk, useSession, useUser } from "@clerk/react";
import {
  Accordion,
  Avatar,
  AvatarFallback,
  Button,
  Chip,
  Input,
  Label,
  Skeleton,
  Switch,
  Tabs,
  toast,
} from "@heroui/react";
import { Bell, CalendarDays, ChevronRight, LogOut, Pencil, Settings, Users } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserRecordingsStats as getUserRecordingsStatsApi } from "@/api/audio";
import { getMyConnections as getMyConnectionsApi } from "@/api/users";
import { getMyMeetsPast as getMyMeetsPastApi } from "@/api/meets";
import { updateUser as updateUserApi } from "@/api/users";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z"
      fill="#4285F4"
    />
    <path
      d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z"
      fill="#34A853"
    />
    <path
      d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"
      fill="#EA4335"
    />
  </svg>
);

const DEFAULT_NOTIFICATIONS = {
  enabled: false,
  email: false,
  push: false,
  whatsapp: false,
};

const AccountPage = () => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { session } = useSession();

  const [selectedTab, setSelectedTab] = useState("settings");
  const [showLogOut, setShowLogout] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [connections, setConnections] = useState([]);
  const [recordingsCount, setRecordingsCount] = useState(0);
  const [loopsCount, setLoopsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [notificationsConfig, setNotificationsConfig] = useState(DEFAULT_NOTIFICATIONS);
  const [savingNotification, setSavingNotification] = useState(null);

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);

  const tabs = useMemo(
    () => [
      { key: "settings", title: "Settings" },
      { key: "details", title: "Details" },
    ],
    [],
  );

  useEffect(() => {
    getUserRecordings();
    getMyConnections();
    getMyMeetsPast();
  }, []);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
  
      setNotificationsConfig({
        ...DEFAULT_NOTIFICATIONS,
        ...user.publicMetadata?.notification,
      });
    }
  }, [user]);

  if (!user) return null;

  const fullName = user.fullName || user.username || "Your Profile";
  const email = user.primaryEmailAddress?.emailAddress ?? "—";
  const roleLabel = user?.publicMetadata?.role || "Member";

  async function getUserRecordings() {
    const token = await session.getToken();

    getUserRecordingsStatsApi(user.id, token).then((_resp) => {
      if (_resp.data.Result === "OK") {
        setRecordingsCount(_resp.data.TotalAudioFiles);
      }
    });
  }

  async function getMyConnections() {
    setIsLoading(true);
    setFetchError(false);
    try {
      const token = await session?.getToken();
      const _resp = await getMyConnectionsApi(token);
      if (_resp.data.Result === "OK") {
        setConnections(_resp.data.Records || []);
      } else {
        throw new Error("Failed to load connections");
      }
    } catch (err) {
      console.log(err);
      setFetchError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function getMyMeetsPast() {
    setIsLoading(true);
    const token = await session.getToken();
    getMyMeetsPastApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          const records = _resp.data.Records;
          setLoopsCount(records.length);
        }
      })
      .catch((_resp) => {
        console.error(_resp);
        setFetchError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  async function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      await user.setProfileImage({ file });
      toast.success("Profile photo updated!");
    } catch (err) {
      const msg = err?.errors?.[0]?.message || err?.message || "Could not update photo.";
      toast.danger(msg);
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  }

  function onEditClick() {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEditing(true);
      setSelectedTab("details");
    }
  }

  function onCancelEdit() {
    setFirstName(user?.firstName || "");
    setLastName(user?.lastName || "");
    setEditing(false);
  }

  async function onSaveEdit() {
    setSaving(true);
    try {
      await user.update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      toast.success("Profile updated!");
      setEditing(false);
    } catch (err) {
      const msg = err?.errors?.[0]?.message || err?.message || "Could not update profile.";
      toast.danger(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut(() => navigate("/sign-in"));
    } catch (err) {
      console.error("Sign out failed:", err);
      toast.danger("Could not sign out. Please try again.");
      setSigningOut(false);
    }
  }

  async function handleNotificationUpdate(notificationType, value) {
    const previousConfig = notificationsConfig;
    const nextConfig = { ...notificationsConfig, [notificationType]: value };

    setNotificationsConfig(nextConfig);
    setSavingNotification(notificationType);

    try {
      const token = await session.getToken();
      const payload = {
        id: user.id,
        first_name: user.firstName,
        last_name: user.lastName,
        public_metadata: {
          notification: nextConfig,
        },
      };

      const _resp = await updateUserApi(payload, token);
      if (_resp.data.Result !== "SUCCESS") {
        throw new Error(_resp.data?.Message || "Update failed");
      }

      const notificationTypeConfig = {
        push: 'Push Notifcations',
        email: 'Email Notifications',
        enabled: 'Notifcations',
        whatsapp: 'WhatsApp Notifications'
      }

      toast.success(`Updated ${notificationTypeConfig[notificationType]}`);
      await user.reload();
    } catch (err) {
      console.log(err);
      setNotificationsConfig(previousConfig);
      toast.danger("Couldn't update notification settings. Please try again.");
    } finally {
      setSavingNotification(null);
    }
  }

  return (
    <>
      <div className="flex gap-6 flex-col items-center pb-40 mt-16">
        <div className="relative">
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhoto}
            className="block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Avatar
              variant="soft"
              color="accent"
              size="lg"
              className="border-accent dark-linear-bg dark:border-secondary-border border-2 size-20 rounded-full"
            >
              {user.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt={fullName}
                  className={`size-full rounded-full object-cover transition-opacity ${uploadingPhoto ? "opacity-40" : "opacity-100"}`}
                />
              ) : (
                <AvatarFallback className="font-bold text-2xl dark:text-secondary">
                  {getUserInitials(fullName)}
                </AvatarFallback>
              )}
            </Avatar>
            {uploadingPhoto && (
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30">
                <svg className="animate-spin size-6 text-white" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhoto}
            className="size-8 flex absolute bottom-0 -right-2 items-center justify-center rounded-full bg-background dark:bg-secondary dark:border-secondary-border"
          >
            <Pencil className="fill-accent text-accent" size={16} />
          </button>
        </div>

        <div className="flex flex-col items-center gap-4">
          <span className="text-2xl font-bold">{fullName}</span>
          <Chip variant="soft" className="bg-tertiary/15 account-tier-badge">
            <Chip.Label className="flex gap-2 items-center flex-row text-tertiary">
              <Users size={12} className="fill-tertiary text-tertiary" /> {roleLabel}
            </Chip.Label>
          </Chip>
        </div>

        <div className="grid gap-4 grid-cols-3 divide-x divide-glass-foreground">
          <div className="flex flex-col items-center gap-0 pr-4" onClick={() => navigate("/past")}>
            {isLoading ? (
              <Skeleton className="h-7 w-8 rounded-md dark:bg-white/20" />
            ) : (
              <span className="font-bold text-xl">{loopsCount}</span>
            )}
            <span className="text-[11px] font-medium text-muted">Loops</span>
          </div>

          <div className="flex flex-col items-center gap-0 pr-4" onClick={() => navigate("/network")}>
            {isLoading ? (
              <Skeleton className="h-7 w-8 rounded-md dark:bg-white/20" />
            ) : (
              <span className="font-bold text-xl">{connections.length}</span>
            )}
            <span className="text-[11px] font-medium text-muted">People</span>
          </div>

          <div className="flex flex-col items-center gap-0" onClick={() => toast.warning("Under development")}>
            {isLoading ? (
              <Skeleton className="h-7 w-8 rounded-md dark:bg-white/20" />
            ) : (
              <span className="font-bold text-xl">{recordingsCount}</span>
            )}
            <span className="text-[11px] font-medium text-muted">Recordings</span>
          </div>
        </div>

        {fetchError && (
          <div className="flex flex-col items-center gap-3 py-6 text-center text-glass-foreground">
            <p className="font-semibold">Something went wrong!</p>
            <p className="text-sm text-glass-foreground-muted">We couldn't find your profile statistics</p>
            <button
              className="text-sm underline"
              onClick={() => { getMyConnections(); getMyMeetsPast(); }}
            >
              Try again
            </button>
          </div>
        )}

        <div className="w-full">
          <Tabs
            className="w-full min-w-full"
            selectedKey={selectedTab}
            onSelectionChange={(key) => setSelectedTab(String(key))}
          >
            <Tabs.ListContainer className="bg-transparent ">
              <Tabs.List className="w-full bg-[#C2C7D433]! dark:bg-[#C2C7D433] border-[#7A8BB614] border-2 rounded-xl">
                {tabs.map((tab) => (
                  <Tabs.Tab
                    id={tab.key}
                    key={tab.key}
                    className="text-xs font-normal data-selected:text-black! dark:text-[#C5C5C5] data-selected:font-bold "
                  >
                    {tab.title}
                    <Tabs.Indicator className="rounded-lg dark:bg-secondary" />
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs.ListContainer>

            <Tabs.Panel id={"settings"} key={"settings"} className="px-0 flex flex-col gap-2">
              <Accordion className={"flex flex-col gap-2"} hideSeparator>
                <div className="rounded-[18px] dark:border-white/0 overflow-hidden">
                  <Accordion.Item className={"profile-settings-accordion bg-surface/80"}>
                    <Accordion.Heading className="">
                      <Accordion.Trigger className={"flex gap-2 px-4.5 py-2.5 rounded-3xl"}>
                        <div className="flex items-center justify-center size-10 glass-box-shadow bg-surface dark-linear-bg rounded-full">
                          <Bell size={16} className="text-[#838383] dark:text-secondary" />
                        </div>
                        <span>Notifications</span>
                        <Accordion.Indicator />
                      </Accordion.Trigger>
                    </Accordion.Heading>

                    <Accordion.Panel className={""}>
                      <Accordion.Body className="flex flex-col gap-3">
                        <Switch
                          className={"w-full"}
                          isSelected={notificationsConfig.enabled}
                          onChange={(value) => handleNotificationUpdate("enabled", value)}
                          isDisabled={savingNotification !== null}
                        >
                          <Switch.Content className="flex text-foreground flex-row w-full justify-between">
                            Enable notifications
                            <Switch.Control>
                              <Switch.Thumb color="accent" />
                            </Switch.Control>
                          </Switch.Content>
                        </Switch>
                        <Switch
                          className={"w-full"}
                          isSelected={notificationsConfig.email}
                          onChange={(value) => handleNotificationUpdate("email", value)}
                          isDisabled={!notificationsConfig.enabled || savingNotification !== null}
                        >
                          <Switch.Content className="flex text-foreground flex-row w-full justify-between">
                            Email
                            <Switch.Control>
                              <Switch.Thumb color="accent" />
                            </Switch.Control>
                          </Switch.Content>
                        </Switch>
                        <Switch
                          className={"w-full"}
                          isSelected={false}
                          isDisabled
                        >
                          <Switch.Content className="flex text-foreground flex-row w-full justify-between">
                            Push (not available yet)
                            <Switch.Control>
                              <Switch.Thumb color="accent" />
                            </Switch.Control>
                          </Switch.Content>
                        </Switch>
                        <Switch
                          className={"w-full"}
                          isSelected={false}
                          isDisabled
                        >
                          <Switch.Content className="flex text-foreground flex-row w-full justify-between">
                            WhatsApp (not available yet)
                            <Switch.Control>
                              <Switch.Thumb color="accent" />
                            </Switch.Control>
                          </Switch.Content>
                        </Switch>
                      </Accordion.Body>
                    </Accordion.Panel>
                  </Accordion.Item>
                </div>

                <div className="rounded-[18px] border-0 dark:border-white/30 shiny-shadow-sm overflow-hidden">
                  <Accordion.Item className={"profile-settings-accordion bg-surface/80"}>
                    <Accordion.Heading className="">
                      <Accordion.Trigger className={"flex gap-2 px-4.5 py-2.5 "}>
                        <div className="flex items-center justify-center bg-surface size-10 glass-box-shadow dark-linear-bg rounded-full">
                          <CalendarDays size={16} className="text-[#838383] dark:text-secondary" />
                        </div>
                        <span>Connected Calendars</span>
                        <Accordion.Indicator />
                      </Accordion.Trigger>
                    </Accordion.Heading>

                    <Accordion.Panel className={""}>
                      <Accordion.Body className="mt-2">
                        <div className="flex justify-between">
                          <div className="flex items-center gap-4 text-foreground font-semibold">
                            <div className="flex items-center gap-4">
                              <GoogleIcon />
                              <span>Google</span>
                            </div>
                            <span className="text-accent text-xs">Connected</span>
                          </div>

                          <div>
                            <Settings size={20} />
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Panel>
                  </Accordion.Item>
                </div>
              </Accordion>

              <Button
                className={"rounded-[18px] justify-between px-4.5 !py-5.5 h-15"}
                fullWidth
                onPress={handleSignOut}
                isDisabled={signingOut}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center size-10 bg-black/10 icon-btn-shadow rounded-full">
                    <LogOut size={12} className="text-glass-foreground size-4" />
                  </div>

                  <span>{signingOut ? "Signing out..." : "Sign Out"}</span>
                </div>

                <ChevronRight size={10} className="size-4" />
              </Button>
            </Tabs.Panel>

            <Tabs.Panel id="details" className="pb-20">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="Jane"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    isDisabled={!editing}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    isDisabled={!editing}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} isDisabled />
                </div>

                {editing ? (
                  <div className="flex gap-2 pt-2">
                    <Button variant="secondary" fullWidth onPress={onCancelEdit} isDisabled={saving}>
                      Cancel
                    </Button>
                    <Button fullWidth onPress={onSaveEdit} isDisabled={saving}>
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </div>
                ) : (
                  <Button fullWidth onPress={onEditClick} className="mt-2">
                    <Pencil className="size-4" />
                    Edit Details
                  </Button>
                )}
              </div>
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default AccountPage;
